import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchOrganizerEventById, updateEvent } from '../apiEndpoints';
import { Button } from '@heroui/react';
import Plus from '@/icons/Plus';
import FAQModal from './FAQModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import Trash from '@/icons/Trash';
import EditIcon from '@/icons/EditIcon';
import { btnGhost, surface } from '@/components/ui/design';

interface FAQ {
    question: string;
    answer: string;
}

interface FAQsTabProps {
    eventId: string;
}

export default function FAQsTab({ eventId }: FAQsTabProps) {
    const [faqs, setFaqs] = useState<FAQ[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [faqToDelete, setFaqToDelete] = useState<{ index: number; faq: FAQ } | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [mobileMenuIdx, setMobileMenuIdx] = useState<number | null>(null);

    const queryClient = useQueryClient();

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Element;
            if (mobileMenuIdx !== null && !target.closest('.mobile-faq-menu-container')) {
                setMobileMenuIdx(null);
            }
        }
        if (mobileMenuIdx !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [mobileMenuIdx]);

    // Fetch event data to get current FAQs
    const { data: eventData } = useQuery({
        queryKey: ['organizerEvent', eventId],
        queryFn: () => fetchOrganizerEventById(eventId),
        enabled: !!eventId,
    });

    // Initialize FAQs from event data
    React.useEffect(() => {
        if (eventData?.data?.faqs) {
            setFaqs(eventData.data.faqs);
        }
    }, [eventData]);

    // Update event mutation
    const updateEventMutation = useMutation({
        mutationFn: async (updatedFaqs: FAQ[]) => {
            setLoading(true);
            setError('');

            const updateData = {
                faqs: JSON.stringify(updatedFaqs)
            };

            return await updateEvent(eventId, updateData);
        },
        onSuccess: () => {
            setLoading(false);
            setError('');
            queryClient.invalidateQueries({ queryKey: ['organizerEvent', eventId] });
            handleCloseModal();
        },
        onError: (err: any) => {
            setLoading(false);
            setError(err?.message || 'Failed to update FAQs');
        },
    });

    const handleAddFaq = () => {
        setEditingFaq(null);
        setEditingIndex(null);
        setIsModalOpen(true);
    };

    const handleEditFaq = (index: number) => {
        setEditingFaq(faqs[index]);
        setEditingIndex(index);
        setIsModalOpen(true);
    };

    const handleSaveFaq = (faq: FAQ) => {
        if (editingIndex !== null) {
            // Editing existing FAQ
            const updatedFaqs = faqs.map((f, i) => i === editingIndex ? faq : f);
            setFaqs(updatedFaqs);
            updateEventMutation.mutate(updatedFaqs);
        } else {
            // Adding new FAQ
            const updatedFaqs = [...faqs, faq];
            setFaqs(updatedFaqs);
            updateEventMutation.mutate(updatedFaqs);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingFaq(null);
        setEditingIndex(null);
    };

    const handleDuplicateFaq = (index: number) => {
        const faqCopy = { ...faqs[index] };
        const updatedFaqs = [...faqs.slice(0, index + 1), faqCopy, ...faqs.slice(index + 1)];
        setFaqs(updatedFaqs);
        updateEventMutation.mutate(updatedFaqs);
    };

    const handleDeleteFaq = (index: number) => {
        setFaqToDelete({ index, faq: faqs[index] });
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = () => {
        if (faqToDelete) {
            const updatedFaqs = faqs.filter((_, i) => i !== faqToDelete.index);
            setFaqs(updatedFaqs);
            updateEventMutation.mutate(updatedFaqs);
            setDeleteConfirmOpen(false);
            setFaqToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmOpen(false);
        setFaqToDelete(null);
    };

    return (
        <div className="pt-0 pb-8">
            <div className="mb-6 hidden lg:block">
                <div className="text-[clamp(28px,4vw,48px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">FAQ</div>
            </div>

            <button
                onClick={handleAddFaq}
                className={`${btnGhost} hidden lg:inline-flex mb-8`}
            >
                <Plus width={14} height={14} /> Add New FAQ
            </button>
            <button
                onClick={handleAddFaq}
                className={`${btnGhost} lg:hidden flex w-full mb-8`}
            >
                <Plus width={14} height={14} /> Add New FAQ
            </button>

            <div className="mb-4 lg:hidden">
                <h3 className="text-[28px] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">FAQ</h3>
            </div>

            {error && (
                <div className="rounded-full bg-error95 px-4 py-2.5 text-sm font-semibold text-error40 dark:bg-error50/15 dark:text-error70 mb-4">
                    {error}
                </div>
            )}

            {loading && (
                <div className="rounded-full bg-primary/10 px-4 py-2.5 text-sm font-semibold text-primary dark:bg-nightAccent/15 dark:text-nightAccent mb-4">
                    Updating FAQs...
                </div>
            )}

            {/* FAQs List */}
            <div className="space-y-3">
                {faqs.length === 0 ? (
                    <div className="rounded-[28px] bg-grey99 px-6 py-14 text-center dark:bg-white/[0.04]">
                        <div className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white mb-2">No FAQs added yet</div>
                        <div className="text-sm text-grey40 dark:text-nightMuted">Click "Add FAQ" to get started</div>
                    </div>
                ) : (
                    faqs.map((faq, index) => (
                        <div key={index} className={`${surface} p-5 sm:px-6`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white mb-1.5 break-words">{faq.question}</div>
                                    <div className="text-sm leading-relaxed text-grey40 dark:text-nightMuted break-words">{faq.answer}</div>
                                </div>
                                <div className="relative flex-shrink-0 mobile-faq-menu-container lg:hidden">
                                    <button
                                        type="button"
                                        className="flex h-10 w-10 items-center justify-center rounded-full bg-white ring-1 ring-black/[0.06] text-grey10 dark:bg-white/[0.06] dark:ring-white/10 dark:text-white"
                                        onClick={() => setMobileMenuIdx(v => v === index ? null : index)}
                                        aria-label="FAQ options"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                            <circle cx="12" cy="5" r="2" />
                                            <circle cx="12" cy="12" r="2" />
                                            <circle cx="12" cy="19" r="2" />
                                        </svg>
                                    </button>
                                    {mobileMenuIdx === index && (
                                        <div className="absolute right-0 top-full mt-2 z-50 min-w-[160px] rounded-2xl bg-white p-1.5 shadow-lg ring-1 ring-black/[0.06] dark:bg-nightElevated dark:ring-white/10">
                                            <button
                                                type="button"
                                                className="w-full rounded-full text-left px-4 py-2 text-sm font-semibold text-grey10 dark:text-white hover:bg-grey99 dark:hover:bg-white/10"
                                                onClick={() => { setMobileMenuIdx(null); handleEditFaq(index); }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="w-full rounded-full text-left px-4 py-2 text-sm font-semibold text-grey10 dark:text-white hover:bg-grey99 dark:hover:bg-white/10"
                                                onClick={() => { setMobileMenuIdx(null); handleDuplicateFaq(index); }}
                                            >
                                                Duplicate
                                            </button>
                                            <button
                                                type="button"
                                                className="w-full rounded-full text-left px-4 py-2 text-sm font-semibold text-error50 hover:bg-error95 dark:text-error60 dark:hover:bg-error50/15"
                                                onClick={() => { setMobileMenuIdx(null); handleDeleteFaq(index); }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="hidden lg:flex gap-2 flex-shrink-0">
                                    <Button
                                        onClick={() => handleDeleteFaq(index)}
                                        variant="light"
                                        size="sm"
                                        isIconOnly
                                        className="h-10 w-10 min-w-0 rounded-full bg-error95 text-error50 hover:bg-error90 dark:bg-error50/15 dark:text-error60 dark:hover:bg-error50/25"
                                        startContent={
                                            <Trash />
                                        }
                                    >

                                    </Button>
                                    <Button
                                        onClick={() => handleDuplicateFaq(index)}
                                        variant="light"
                                        size="sm"
                                        isIconOnly
                                        className="h-10 w-10 min-w-0 rounded-full bg-white ring-1 ring-black/[0.06] text-grey10 hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:text-white dark:hover:bg-white/10"
                                        startContent={
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <rect x="9" y="9" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        }
                                    >

                                    </Button>
                                    <Button
                                        onClick={() => handleEditFaq(index)}
                                        variant="light"
                                        size="sm"
                                        isIconOnly
                                        className="h-10 w-10 min-w-0 rounded-full bg-white ring-1 ring-black/[0.06] text-grey10 hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:text-white dark:hover:bg-white/10"
                                        startContent={
                                            <EditIcon />
                                        }
                                    >

                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* FAQ Modal */}
            <FAQModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveFaq}
                editingFaq={editingFaq}
                loading={loading}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmModal
                isOpen={deleteConfirmOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Delete FAQ"
                message="Are you sure you want to delete this FAQ? This action cannot be undone."
                itemName={faqToDelete ? `Q: ${faqToDelete.faq.question}` : undefined}
                loading={loading}
            />
        </div>
    );
}